import { gsap } from 'gsap';
import {
  type MutableRefObject,
  type Ref,
  type RefObject,
  useImperativeHandle,
  useRef,
} from 'react';

export interface AnimationComponentRef {
  createTimeline: () => gsap.core.Timeline;
}

export interface TimelineAnimationStep {
  target: RefObject<HTMLElement>;
  from?: {
    x?: number;
    y?: number;
    opacity?: number;
    scale?: number | (() => number);
    xPercent?: number;
    yPercent?: number;
  };
  to: {
    x?: number | (() => number);
    y?: number | (() => number);
    opacity?: number;
    scale?: number | (() => number);
    duration?: number;
    ease?: string;
    xPercent?: number;
    yPercent?: number;
    rotate?: number;
    /** 애니메이션 시작 시 실행될 콜백 */
    onStart?: () => void;
    /** 애니메이션 완료 시 실행될 콜백 */
    onComplete?: () => void;
  };
  /** 예시) "+=0.5", "-=0.3", 1.5, "<" 등 */
  position?: string | number;
}

interface TimelineOptions {
  trigger?: RefObject<HTMLElement>;
  start?: string;
  end?: string;
  scrub?: boolean | number;
  pin?: boolean;
  markers?: boolean;
  delay?: number;
  onComplete?: () => void;
  onUpdate?: (progress: number) => void;
}

interface UseTimelineAnimationProps {
  timelineRef: Ref<AnimationComponentRef>;
  steps: TimelineAnimationStep[];
  options: TimelineOptions;
}

export function useTimelineAnimation({
  timelineRef,
  steps,
  options,
}: UseTimelineAnimationProps): MutableRefObject<HTMLDivElement | null> {
  const triggerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    timelineRef,
    () => ({
      createTimeline: () => {
        const tl = gsap.timeline({
          delay: options.delay || 0,
          onComplete: options.onComplete,
          scrollTrigger: {
            trigger: options.trigger?.current || triggerRef.current,
            start: options.start || 'top top',
            end: options.end || 'bottom bottom',
            scrub: options.scrub !== undefined ? options.scrub : 1,
            pin: options.pin || false,
            markers: options.markers || false,
            onUpdate: (self) => {
              if (options.onUpdate) {
                options.onUpdate(self.progress);
              }
            },
          },
        });

        steps.forEach((step) => {
          if (!step.target.current) {
            return;
          }

          if (step.from) {
            // fromTo 애니메이션
            tl.fromTo(
              step.target.current,
              step.from,
              step.to,
              step.position || '>',
            );
          } else {
            // to 애니메이션
            tl.to(step.target.current, step.to, step.position || '>');
          }
        });

        return tl;
      },
    }),
    [steps, options],
  );

  return triggerRef;
}
